import React, { useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { getSubmit } from '@ui-seeker/utils'
import { useNavigate } from 'react-router-dom'

import { AppAcrissAutocomplete, AppAcrissGroupsAutocomplete, AppCategoriesAutocomplete, KindPillsField } from '@components'
import { CARS, GROUPS_HEADER, NO_GROUPS_TEXT, carsKinds } from '@components/KindPillsField/carKinds'
import { replaceNote } from '@components/KindPillsField/kindPills'
import { BACKEND_APIS, SERVICES_TYPE, USER_ROUTES } from '@constants'
import { dsColorNew } from '@palette'
import { analyzerOptionFilters } from 'Pages/AnalyzersV2/@signals'
import { ALL_CARS, groupState, nextPick, pickLabels, pickOn, pickTab } from 'Pages/AnalyzersV2/@services/carPick'


const GROUPS_URL = `${BACKEND_APIS.CRUD}/user/${SERVICES_TYPE.ACRISS_GROUP}?selectorFormat=true`
const ACRISS_URL = `${BACKEND_APIS.MASTER_DATA}/execution/options/acriss`
const CATEGORIES_URL = `${BACKEND_APIS.MASTER_DATA}/execution/options/categories`

// The company's groups (every user), to know whether there are any, and the Acriss of this execution, to say which
// groups have no prices in it (UX Obs. 7)
// the table's header is redrawn with each answer: the lists are kept per address and filters for the page's life
const LISTS = new Map()

function useList(url, filters, enabled = true) {
	const key = `${url}|${JSON.stringify(filters || {})}`
	const [list, setList] = useState(() => LISTS.get(key) ?? null)
	useEffect(() => {
		if (!enabled) return
		if (LISTS.has(key)) { setList(LISTS.get(key)); return }
		let alive = true
		setList(null)
		getSubmit(url, filters || {})
			.then(res => { const value = Array.isArray(res) ? res : []; LISTS.set(key, value); if (alive) setList(value) })
			.catch(() => alive && setList([]))
		return () => { alive = false }
	}, [key, enabled])
	return list
}

/**
 * "Cars" above the price table of DOR, LOR and Rank Analyzer (CU-869fmpqfm): one or several values of one kind —
 * Acriss · Category · Acriss Group — over the shared `KindPillsField` (UX Obs. 3), multi-select as the BI dashboards'
 * Cars field (v1.2, Curro 2026-10-28). Each change asks again (`onPick`; only the latest answer paints) and the menu
 * stays open until Done. Acriss and Category list this execution's cars (office, site, pass), not narrowed by the band
 * (decision 2).
 */
export default function AnalyzerCarField({ pick, onPick }) {
	const navigate = useNavigate()
	const filters = analyzerOptionFilters.value
	const groups = useList(GROUPS_URL, null)
	// only with groups to judge: the same list the band's Acriss field reads
	const executionAcriss = useList(ACRISS_URL, filters, !!groups?.length)
	const kinds = carsKinds({ groupsCount: groups ? groups.length : null })
	const on = pickOn(pick)
	const labels = on ? pickLabels(pick) : []

	const choose = kind => e => {
		const next = nextPick(pick, kind, e?.target?.value || [], groups || [])
		if (next !== pick) onPick(next)
	}

	const renderKind = (tab, close) => {
		const common = {
			multiple: true, limitTags: 3, fullWidth: true, openOnFocus: true, autoFocus: true,
			value: on && pick.kind === tab ? [...(pick.values || [])] : [], onChange: choose(tab),
			ListboxProps: { style: { maxHeight: 260 } },
		}
		const toSettings = () => { close(); navigate(`${USER_ROUTES.SETTINGS_PATH}?tab=acriss_group`) }
		if (tab === CARS.GROUP && kinds.find(k => k.value === tab)?.empty) {
			return <Typography sx={{ fontSize: 13, color: dsColorNew.textDark, lineHeight: 1.5 }}>
				{NO_GROUPS_TEXT.replace(/Settings › Acriss groups\.$/, '')}
				<Button variant="text" size="small" onClick={toSettings} sx={{ textTransform: 'none', p: '0 2px', minWidth: 0, verticalAlign: 'baseline' }}>Settings › Acriss groups</Button>.
			</Typography>
		}
		if (tab === CARS.GROUP) {
			const state = g => groupState(g, executionAcriss ? executionAcriss.map(o => o.value) : null)
			return <>
				<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle, mb: 1 }}>
					{GROUPS_HEADER.split(' · ')[0]} · <Button variant="text" size="small" onClick={toSettings} sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: 11, letterSpacing: 0.6, fontWeight: 600 }}>{GROUPS_HEADER.split(' · ')[1]}</Button>
				</Typography>
				<AppAcrissGroupsAutocomplete {...common} placeholder="Pick groups"
					getOptionDisabled={o => state(o).disabled}
					renderOption={(props, o) => <Box component="li" {...props} key={o.value} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important' }}>
						<span>{o.label}</span>
						<Typography component="small" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
							{state(o).reason || (o.acriss || []).join(', ')}
						</Typography>
					</Box>}/>
			</>
		}
		if (tab === CARS.ACRISS) return <AppAcrissAutocomplete {...common} placeholder="Pick Acriss codes" urlAjax={ACRISS_URL} filters={filters}/>
		return <AppCategoriesAutocomplete {...common} label="Category" placeholder="Pick categories" urlAjax={CATEGORIES_URL} filters={filters}/>
	}

	return <KindPillsField
		label="Cars" allText="All cars" clearLabel="All cars" pillsLabel="Pick a car by"
		on={on} labels={labels} under={null}
		kinds={kinds} openTab={() => pickTab(pick)}
		renderKind={renderKind} noteFor={tab => replaceNote({ kind: pick?.kind, on }, tab, labels)}
		onClear={() => onPick(ALL_CARS)}
		width={340}
	/>
}
